import { Button, Input, Modal, Select, Table, Space, DatePicker, theme, Tabs, Upload, Form, Spin, message, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { orderColumns } from './constants';
import "./worklist.css";
import FloatLabel from '../../components/FloatingLabel';
import axiosInstance, { BASE_API } from '../../axios';
import { getUserDetails, hisStatusOptions, makePostCall } from '../../utils/helper';
import { useSites } from '../../hooks/useSites';
import { useModalities } from '../../hooks/useModalities';
import { useBodyParts } from '../../hooks/useBodyParts';
import dayjs from 'dayjs';
import FyzksInput from '../../components/FyzksInput';
import { debounce, throttle } from 'lodash';
import ViewNotes from '../ManageNodes/ViewNotes';
import OrderInfoPopup from '../OrderInfo';
import PdfViewer from '../../components/PdfViewer';
import ShareStudyModal from '../../components/ShareStudyModal';
import DownloadStudyModal from '../../components/DownloadStudyModal';
import TextArea from 'antd/es/input/TextArea';
import TabPane from 'antd/es/tabs/TabPane';
import { useForm } from 'antd/es/form/Form';
// Import shared action handlers
import * as actionHandlers from '../../utils/tableActionHandlers';
import TinyEditor from '../ReportEditor/tinymce-editor';
import { useWebSocketContext } from '../../context/WebSocketContext';
import { getAutoRefreshEnabled, subscribeAutoRefresh } from '../../utils/autoRefreshPubSub';

const { RangePicker } = DatePicker;

const MyWorklist = ({ StyledTabsComponent }) => {
  const { token } = theme.useToken();
  const [orders, setOrders] = useState({ data: [], loading: true, total: 0 });
  const [filters, setFilters] = useState({});
  const userDetails = getUserDetails();
  const [userList, setUserList] = useState([]);
  const [isUserIdle, setIsUserIdle] = useState(false);
  const lastActivityRef = useRef(Date.now());

  // Use dynamic configuration hooks
  const { centers, loading: sitesLoading } = useSites();
  const { modalityOptions, loading: modalitiesLoading } = useModalities();
  const { bodyPartOptions, loading: bodyPartsLoading } = useBodyParts();
  // Set the default value to [yesterday at 00:00, today at 23:59]
  const today = dayjs();
  const yesterday = dayjs().subtract(1, 'days');
  const [dateRange, setDateRange] = useState([
    yesterday.hour(0).minute(0),
    today.hour(23).minute(59)
  ]);
  const [refreshDisabled, setRefreshDisabled] = useState(false);

  const [viewNotesModal, setViewNotesModal] = useState({ visible: false, details: null });
  const [selectedNote, setSelectedNote] = useState({});
  const [noteEditActive, setNoteEditActive] = useState(false);
  const [saveNoteHandler, setSaveNoteHandler] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(getAutoRefreshEnabled());

  // WebSocket context for real-time updates
  const wsContext = useWebSocketContext();

  useEffect(() => {
    const unsubscribe = subscribeAutoRefresh((enabled) => {
      setAutoRefreshEnabled(enabled);
    });

    return () => unsubscribe();
  }, []);

  // Handle priority filter changes
  const handlePriorityChange = (priority) => {
    if (priority === 'all') {
      // Remove po_priority from filters when "All priorities" is selected
      const temp_filters = { ...filters };
      delete temp_filters['po_priority'];
      setFilters(temp_filters);
      setSelectedPriority(null);
    } else {
      // Set specific priority filter
      handleFilterChange('po_priority', priority);
      setSelectedPriority(priority);
    }
  };

  // Additional state for new functionality (matching PACS page)
  const [addFileModal, setAddFileModal] = useState({ visible: false, data: null, notes: null, modalType: 'upload', file: null, fileType: null });
  const [orderInfoModal, setOrderInfoModal] = useState({});
  const [viewerModal, setViewerModal] = useState({ visible: false, data: {} });
  const [printLoading, setPrintLoading] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [selectedStudyForShare, setSelectedStudyForShare] = useState(null);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [selectedStudyForDownload, setSelectedStudyForDownload] = useState(null);
  const [receiverForm] = useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  const [editReportModal, setEditReportModal] = useState({ visible: false, reportData: null, patientData: null });

  // Series selection modal state
  const [seriesSelectionModal, setSeriesSelectionModal] = useState({
    visible: false,
    studyUID: null,
    record: null,
    seriesList: [],
    selectedSeries: [],
    loading: false,
  });

  useEffect(() => {
    // getOrdersList();
    getUsersList();
  }, []);

  useEffect(() => {
    console.log("Filters changed", filters);
     // Reset to page 1 when filters change
    if (Object.keys(filters).length > 0 || dateRange) {
      setPagination({ current: 1, pageSize: pagination.pageSize });
    }
    debouncedFilter(filters);
  }, [filters]);

  useEffect(() => {
    console.log("date changed", filters);
    // if (Object.keys(filters).length) {
    debouncedFilter(filters);
    // }
  }, [dateRange]);

  useEffect(() => {
      const unsubscribe = subscribeAutoRefresh((enabled) => {
        setAutoRefreshEnabled(enabled);
      });

    return () => unsubscribe();

  }, []);

  // Auto-refresh effect
  useEffect(() => {
    let intervalId;

    if (autoRefreshEnabled && !isUserIdle) {
      intervalId = setInterval(() => {
        // Only refresh if not currently loading and filters are applied
        if (!orders.loading) {
          console.log('Auto-refreshing worklist...');
          // Refresh current page only - maintains user position
          filterResults(filters, pagination.current, pagination.pageSize);
        }
      }, 5 * 60 * 1000); // 5 minutes
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefreshEnabled, isUserIdle, orders.loading, filters, dateRange, pagination.current, pagination.pageSize]);


  // Subscribe to WebSocket messages for real-time study updates
  useEffect(() => {
    const unsubscribe = wsContext.subscribe((data) => {
      try {
        // Handle study update notifications using state values directly
        if (data.type === 'study_update' && autoRefreshEnabled && !isUserIdle) {
          console.log('[PACS] Received WebSocket message:', data);
          // Only refresh if not currently loading
          if (!orders.loading) {
            console.log('Auto-refreshing studies list...');
            filterResults(filters, pagination.current, pagination.pageSize);
          }
        }
      } catch (e) {
        console.error('[PACS] Error in WS handler:', e);
      }
    });
      return () => unsubscribe();
  }, [wsContext, autoRefreshEnabled, isUserIdle, orders.loading, filters, pagination]);
  // Idle detection effect
  useEffect(() => {
    const IDLE_TIME = 5 * 60 * 1000; // 5 minutes of inactivity

    const resetIdleTimer = () => {
      lastActivityRef.current = Date.now();
      if (isUserIdle) {
        setIsUserIdle(false);
      }
    };

    // Throttle the idle timer reset to prevent excessive function calls
    const throttledResetIdleTimer = throttle(resetIdleTimer, 1000, {
      leading: true,
      trailing: false
    });

    const checkIdleStatus = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;

      if (timeSinceActivity >= IDLE_TIME && !isUserIdle) {
        setIsUserIdle(true);
        console.log('User is idle, pausing auto-refresh');
      } else if (timeSinceActivity < IDLE_TIME && isUserIdle) {
        setIsUserIdle(false);
        console.log('User is active, resuming auto-refresh');
      }
    };

    // Events to track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, throttledResetIdleTimer, true);
    });

    // Check idle status every 30 seconds
    const idleCheckInterval = setInterval(checkIdleStatus, 30 * 1000);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledResetIdleTimer, true);
      });
      clearInterval(idleCheckInterval);
    };
  }, [isUserIdle]);

  // const debouncedFilter = debounce(() => { filterResults() }, 300);

  const debouncedFilter = useCallback(
    debounce((tempFilters) => {
      console.log('Debounced value:');
      // When filters change via debounce, use page 1 (already set in useEffect)
      filterResults(tempFilters, 1, pagination.pageSize);
    }, 500),
    [dateRange, pagination.pageSize]
  );

  const getUsersList = () => {
    makePostCall('/user-list', {}).then(res => {
      setUserList(res.data?.data || []);
    })
      .catch(e => {
        console.log(e);
        setUserList([]);
      })
  }


  const getOrdersList = async () => {
    makePostCall('/my-worklist', {
      role: userDetails?.user_type,
      user_id: getUserDetails()?.username,
    })
      .then(res => {
        setOrders({ data: res.data?.data || [], loading: false })
      })
      .catch(e => {
        console.log(e);
        setOrders({ data: [], loading: false })
      });
  }

  // Removed modal functionality - everything opens in new tab now

  const filterResults = (tempFilters, page = 1, pageSize = 20) => {
    setOrders({ ...orders, loading: true, data: [] });
    const payload = {
      ...tempFilters,
      role: userDetails?.user_type,
      user_id: getUserDetails()?.username,
      page: page - 1, // Backend expects 0-indexed page
      limit: pageSize
    };

    if (dateRange) {
      payload['from_date'] = dayjs(dateRange[0]).format('YYYYMMDDHHmmss');
      payload['to_date'] = dayjs(dateRange[1]).format('YYYYMMDDHHmmss');
      payload['po_study_date'] = dateRange;
    }

    makePostCall('/my-worklist', payload)
      .then(res => {
        setOrders({
          data: res.data?.data || [],
          loading: false,
          total: res.data?.total || 0
        })
      })
      .catch(e => {
        console.log(e);
        setOrders({ data: [], loading: false, total: 0 })
      });
  }

  const handleFilterChange = (key, value) => {
    const temp_filters = { ...filters };
    temp_filters[key] = value;
    setFilters(temp_filters);
  }

  const refreshScanStatus = () => {
    setRefreshDisabled(true); // Disable the button
    setTimeout(() => {
      setRefreshDisabled(false); // Re-enable the button after 5 seconds
    }, 20000);
    axiosInstance.get(BASE_API + '/update-status')
      .then(res => {
        // console.log("res", res);
        // Refresh with current pagination
        filterResults(filters, pagination.current, pagination.pageSize);
      })
      .catch(e => {
        console.log(e);
      })
  }

  const debouncedRefresh = useCallback(
    debounce(() => {
      console.log('Debounced value:');
      refreshScanStatus();
    }, 200),
    []
  );

  const statusOptions = userDetails?.user_type === 'doc' ? [
    { label: 'SIGNEDOFF', value: 'SIGNEDOFF' },
  ] :
    [
      { label: 'PENDING', value: 'PENDING' },
      { label: 'SCANNED', value: 'SCANNED' },
      { label: 'DRAFTED', value: 'DRAFTED' },
      { label: 'SIGNEDOFF', value: 'SIGNEDOFF' },
    ]

  // All options now come from dynamic hooks:
  // siteOptions, modalityOptions, bodyPartOptions

  const reportedByOptions = useMemo(() => {
    return userList?.map((user) => ({ label: user.user_fullname, value: user.username }))
  }, [userList]);

  const viewNotes = (rec) => {
    console.log("view notes", rec);
    const firstNote = rec.note_id || {};
    setSelectedNote(firstNote)
    setNoteEditActive(false);
    setViewNotesModal({ visible: true, details: rec })
  }

  const handleNoteSelection = useCallback((note, _record) => {
    setSelectedNote(prevSelectedNote => {
      const previousNoteId = (prevSelectedNote as any)?.rn_id || null;
      const nextNoteId = note?.rn_id || null;
      const previousNoteText = (prevSelectedNote as any)?.rn_notes || '';
      const nextNoteText = note?.rn_notes || '';

      if (previousNoteId === nextNoteId && previousNoteText === nextNoteText) {
        return prevSelectedNote;
      }

      return note || {};
    });
  }, [])

  const clearFilters = () => {
    setFilters({});
    setDateRange(null);
    setSelectedPriority(null);
    setPagination({ current: 1, pageSize: pagination.pageSize });
    filterResults({}, 1, pagination.pageSize);
  }

  // Action handlers using shared utilities
  const openViewer = (record) => actionHandlers.openViewer(record, userDetails);
  const openRadDesk = (record) => actionHandlers.openRadDesk(record, userDetails);
  const openReport = (record) => actionHandlers.openReport(record, userDetails);
  const printReport = (rec) => actionHandlers.printReport(rec, setPrintLoading);
  const downloadDocxReport = (rec) => actionHandlers.downloadDocxReport(rec, setPrintLoading);
  const viewReport = (rec) => actionHandlers.viewReport(rec, setPrintLoading, setViewerModal);

  const addFile = (rec) => {
    setAddFileModal({ visible: true, data: rec, modalType: 'upload' });
  }

  const uploadRisNotes = () => actionHandlers.uploadRisNotes(addFileModal, setAddFileModal);

  const showOrdDetails = (rec) => {
    setOrderInfoModal({ visible: true, data: rec });
  }

  const closeOrdInfo = () => {
    setOrderInfoModal({ visible: false, data: null });
  }

  const handleShareStudy = (record) => {
    setSelectedStudyForShare(record);
    setShareModalVisible(true);
  };

  const handleDownloadStudy = (record) => {
    setSelectedStudyForDownload(record);
    setDownloadModalVisible(true);
  };

  // Get DICOM config URL helper
  const getConfigUrl = (urlType) => {
    const config = window.config;
    const dicomwebSource = config?.dataSources?.find(
      (source) => source.sourceName === 'dicomweb'
    );
    return (
      dicomwebSource?.configuration?.[urlType] ||
      `https://dev-api.photonpacs.com/dicom/aets/DCM4CHEE/${urlType === 'wadoUriRoot' ? 'wado' : 'rs'}`
    );
  };

  // Fetch DICOM data helper
  const fetchDicomData = async (url) => {
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  };

  // Fetch series for a study
  const fetchSeriesForStudy = async (studyUID) => {
    setSeriesSelectionModal(prev => ({ ...prev, loading: true }));

    try {
      const qidoBase = getConfigUrl('qidoRoot');
      const seriesData = await fetchDicomData(`${qidoBase}/studies/${studyUID}/series`);

      const seriesList = seriesData
        .map((series) => ({
          seriesUID: series['0020000E']?.Value?.[0],
          seriesDescription: series['0008103E']?.Value?.[0] || 'Unknown Series',
          modality: series['00080060']?.Value?.[0] || 'OT',
          seriesNumber: parseInt(series['00200011']?.Value?.[0]) || 0,
          seriesDate: series['00080021']?.Value?.[0],
          bodyPart: series['00180015']?.Value?.[0] || '',
          numInstances: parseInt(series['00201209']?.Value?.[0]) || 0,
        }))
        .filter((s) => s.seriesUID) // Filter out any series without UID
        .sort((a, b) => a.seriesNumber - b.seriesNumber); // Sort by series number

      setSeriesSelectionModal(prev => ({
        ...prev,
        seriesList,
        selectedSeries: seriesList.map((s) => s.seriesUID), // Select all by default
        loading: false,
      }));
    } catch (error) {
      message.error(`Failed to fetch series: ${error.message}`);
      setSeriesSelectionModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle series selection button click
  const handleSelectSeries = (record) => {
    if (!record?.ps_study_uid) {
      message.error('Study UID not found');
      return;
    }

    setSeriesSelectionModal({
      visible: true,
      studyUID: record.ps_study_uid,
      record: record,
      seriesList: [],
      selectedSeries: [],
      loading: false,
    });

    fetchSeriesForStudy(record.ps_study_uid);
  };

  // Toggle series selection
  const toggleSeriesSelection = (seriesUID) => {
    setSeriesSelectionModal(prev => ({
      ...prev,
      selectedSeries: prev.selectedSeries.includes(seriesUID)
        ? prev.selectedSeries.filter((uid) => uid !== seriesUID)
        : [...prev.selectedSeries, seriesUID],
    }));
  };

  // Select/Deselect all series
  const handleSelectAllSeries = () => {
    setSeriesSelectionModal(prev => ({
      ...prev,
      selectedSeries: prev.seriesList.map((s) => s.seriesUID),
    }));
  };

  const handleDeselectAllSeries = () => {
    setSeriesSelectionModal(prev => ({
      ...prev,
      selectedSeries: [],
    }));
  };

  // Open viewer with selected series
  const handleOpenSelectedSeries = async () => {
    const { studyUID, selectedSeries } = seriesSelectionModal;

    if (selectedSeries.length === 0) {
      message.warning('Please select at least one series');
      return;
    }

    // Build viewer URL with series filter
    const seriesUIDs = selectedSeries.join(',');
    const viewerUrl = `/viewer/viewer?StudyInstanceUIDs=${studyUID}&SeriesInstanceUIDs=${seriesUIDs}`;

    // Open in new window
    window.open(viewerUrl, '_blank');

    // Close modal
    setSeriesSelectionModal({
      visible: false,
      studyUID: null,
      record: null,
      seriesList: [],
      selectedSeries: [],
      loading: false,
    });
  };

  const editSignedOffReport = async (rec) => {
    await actionHandlers.editSignedOffReport(rec, setEditReportModal);
  };

  return (
    <Spin spinning={printLoading}>
    <div>
      {/* Tab bar with action buttons */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div>
          {StyledTabsComponent && StyledTabsComponent()}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.25rem' }}>
          <button
            className="btn-secondary"
            onClick={() => { clearFilters() }}
          >
            Clear Filters
          </button>
          <button
            className="btn-primary"
            onClick={() => filterResults(filters)}
          >
            <SearchOutlined />
            Search Worklist
          </button>
        </div>
      </div>
      <div style={{ padding: '0 1.25rem' }}>
        <div style={{
          background: '#FFFFFF',
          borderRadius: '0.75rem',
          border: '1px solid #E2E8F1',
          padding: '1rem 1.25rem',
          marginBottom: '1rem'
        }}>
          <div className='filters-section'>
        {/* <Space.Compact> */}
        {/* <span style={{ width: 140 }} className='!ms-3'>Patient Name</span> */}
        <div
          className='filters-grid'
          style={{
            '--primary-color': token.colorPrimary
          } as React.CSSProperties}
        >
          {/* Row 1: Patient Name | Priority Pills | Status | Body Part | Site */}
          <FloatLabel label="Patient Name" value={filters['pat_name']}>
            <FyzksInput value={filters['pat_name'] || ''} onChange={(e) => handleFilterChange('pat_name', e.target.value)} />
          </FloatLabel>

          <div className="priority-pills-group">
            <div className="priority-pills-row">
              <button
                className={`priority-pill priority-pill-all ${!selectedPriority ? 'selected' : ''}`}
                onClick={() => handlePriorityChange('all')}
              >
                <span className="pill-text-full">All priorities</span>
                <span className="pill-text-short">All</span>
              </button>
              <button
                className={`priority-pill priority-pill-asap ${selectedPriority === 'ASAP' ? 'selected' : ''}`}
                onClick={() => handlePriorityChange('ASAP')}
              >
                ASAP
              </button>
            </div>
          </div>

          <FloatLabel label="Status" value={filters['po_status']}>
            <Select mode="multiple" allowClear style={{ width: '12.5rem' }} value={filters['po_status']} options={statusOptions} onChange={(val) => handleFilterChange('po_status', val)} />
          </FloatLabel>

          <FloatLabel label="Body Part / Study Desc" value={filters['po_body_part']}>
            <FyzksInput value={filters['po_body_part'] || ''} onChange={(e) => handleFilterChange('po_body_part', e.target.value)} />
          </FloatLabel>

          <FloatLabel label="Site" value={filters['po_site']}>
            <Select allowClear style={{ width: '12.5rem' }} value={filters['po_site']} options={centers} onChange={(val) => handleFilterChange('po_site', val)} />
          </FloatLabel>

          {/* Row 2: Study Date | Priority Pills | Modality | Order No | HIS Status */}
          <FloatLabel label="Study Date" value={filters['study_date']}>
            <RangePicker
              style={{width: '25rem'}}
              size="middle"
              showTime={{
                format: 'HH:mm',
                use12Hours: false,
                defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
              }}
              format="YYYY-MM-DD HH:mm"
              value={dateRange}
              onChange={(val) => {
                if (val) {
                  setDateRange([val[0], val[1]]);
                } else {
                  setDateRange(null);
                }
              }}
            />
          </FloatLabel>

          <div className="priority-pills-group">
            <div className="priority-pills-row">
              <button
                className={`priority-pill priority-pill-normal ${selectedPriority === 'NORMAL' ? 'selected' : ''}`}
                onClick={() => handlePriorityChange('NORMAL')}
              >
                Normal
              </button>
              <button
                className={`priority-pill priority-pill-emergency ${selectedPriority === 'EMERGENCY' ? 'selected' : ''}`}
                onClick={() => handlePriorityChange('EMERGENCY')}
              >
                Emergency
              </button>
            </div>
          </div>

          <FloatLabel label="Modality" value={filters['modality']}>
            <Select mode="multiple" allowClear style={{ width: '12.5rem' }} value={filters['modality']} options={modalityOptions} onChange={(val) => handleFilterChange('modality', val)} />
          </FloatLabel>

          <FloatLabel label="Order No" value={filters['po_ord_no']}>
            <FyzksInput value={filters['po_ord_no'] || ''} onChange={(e) => handleFilterChange('po_ord_no', e.target.value)} />
          </FloatLabel>

          {/*<FloatLabel label="HIS Status" value={filters['po_his_status']}>
            <Select allowClear style={{ width: '12.5rem' }} value={filters['po_his_status']} options={hisStatusOptions} onChange={(val) => handleFilterChange('po_his_status', val)} />
          </FloatLabel>*/}

          {/* Commented out filters for future use */}
          {/*
          <FloatLabel label="Pat ID" value={filters['po_pin']}>
            <FyzksInput width={200} value={filters['po_pin']} onChange={(e) => handleFilterChange('po_pin', e.target.value)} />
          </FloatLabel>
          <FloatLabel label="Acc. No" value={filters['po_acc_no']}>
            <FyzksInput width={200} value={filters['po_acc_no']} onChange={(e) => handleFilterChange('po_acc_no', e.target.value)} />
          </FloatLabel>*/}
          <FloatLabel label="Ref Doc." value={filters['po_ref_doc']}>
            <FyzksInput allowClear value={filters['po_ref_doc'] || ''} onChange={(e) => handleFilterChange('po_ref_doc', e.target.value)} />
          </FloatLabel>
          {/*}
          <FloatLabel label="Reported By" value={filters['po_reported_by']}>
            <Select
              showSearch
              allowClear
              value={filters['po_reported_by']}
              style={{ width: 200 }}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={reportedByOptions}
              onChange={(val) => handleFilterChange('po_reported_by', val)}
            />
          </FloatLabel>
          */}
        </div>
        {/* <Button className='!ms-auto ms-3' type='dashed' danger onClick={() => { debouncedRefresh() }} >Refresh</Button> */}
        </div>
      </div>
      <div className='orders-list'>
        <Table
          tableLayout='fixed'
          style={{ width: '100%' }}
          loading={orders.loading}
          columns={orderColumns({
            openReportEditor: openReport, // Opens in new tab for both patient name and Edit Report icon
            viewNotes,
            openViewer,
            showOrdDetails,
            openRadDesk,
            printReport,
            downloadDocxReport,
            viewReport,
            editSignedOffReport,
            handleShareStudy,
            handleDownloadStudy,
            handleSelectSeries,
            addFile
          })}
          dataSource={orders.data || []}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: orders?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, pageSize) => {
              setPagination({
                current: page,
                pageSize
              });
              // Trigger API call with new page
              filterResults(filters, page, pageSize);
            },
            showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
            position: ['bottomCenter']
          }}
          onRow={(record, rowIndex) => {
            return {
            }
          }}
          scroll={{
            x: 1200,
            y: 'calc(100vh - 27rem)'
          }}
        />

        {
          viewNotesModal && viewNotesModal.visible && (
            <Modal
              open={viewNotesModal.visible}
              onCancel={() => {
                if (noteEditActive) {
                  setNoteEditActive(false);
                  return;
                }

                setViewNotesModal(null);
                setNoteEditActive(false);
              }}
              onOk={async () => {
                if (saveNoteHandler) {
                  const saved = await saveNoteHandler();
                  if (!saved) {
                    return;
                  }
                }
              }}
              style={{ width: '100%', height: '100%' }}
              width={'90%'}
              okButtonProps={{
                style: {
                  marginTop: '20px',
                },
                disabled: !noteEditActive,
              }}
              okText="Save"
              cancelText={noteEditActive ? 'Cancel' : 'Close'}
            >
              <ViewNotes
                handleNoteSelection={handleNoteSelection}
                selectedNote={selectedNote}
                viewNotesModal={viewNotesModal}
                isEditMode={noteEditActive}
                onEditNoteStateChange={setNoteEditActive}
                onSaveNoteRegister={setSaveNoteHandler}
              />
            </Modal>
          )
        }

        {/* Add File Modal */}
        {
          addFileModal && addFileModal?.visible && (
            <Modal
              open={addFileModal?.visible}
              onOk={() => { uploadRisNotes() }}
              onCancel={() => { setAddFileModal({ visible: false, data: null, notes: null, modalType: 'upload', file: null, fileType: null }) }}
              okButtonProps={{
                disabled: (addFileModal?.modalType === 'upload' && !addFileModal?.file) || (addFileModal?.modalType === 'notes' && !addFileModal?.notes),
              }}
            >
              <Tabs
                activeKey={addFileModal.modalType}
                onTabClick={(activeKey) => {
                  if (addFileModal.modalType !== activeKey) {
                    setAddFileModal({ ...addFileModal, modalType: activeKey });
                  }
                }}
              >
                <TabPane key='notes' tab="Notes">
                  <div style={{ padding: '0.5rem 1rem 1rem 1rem' }}>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <label style={{ fontWeight: 500, fontSize: '1rem' }}>
                        Enter Notes / Remarks
                      </label>
                      <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                        Add any additional notes or remarks about this study
                      </p>
                    </div>
                    <TextArea
                      placeholder="Type your notes here..."
                      rows={6}
                      onChange={(e) => {
                        setAddFileModal({ ...addFileModal, notes: e.target.value })
                      }}
                      style={{
                        fontSize: '0.9rem',
                        borderRadius: '0.5rem',
                        border: '1px solid #d9d9d9',
                      }}
                    />
                  </div>
                </TabPane>
                <TabPane key='upload' tab="File Upload">
                  <div style={{ padding: '0.5rem 1rem 1rem 1rem' }}>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <label className='file-box-text'>
                        Select File Type
                      </label>
                      <Select
                        placeholder="Choose file type"
                        options={[
                          { label: 'Prescription', value: 'prescription' },
                          { label: 'Consent', value: 'consent' },
                        ]}
                        style={{ width: '100%' }}
                        onSelect={(val) => { setAddFileModal({ ...addFileModal, fileType: val }) }}
                      />
                    </div>
                    <div>
                      <label className='file-box-text'>
                        Upload PDF File
                      </label>
                      <div
                        className={`upload-pdf-box ${
                          addFileModal.fileType ? 'active' : ''
                        }`}
                      >
                        <Upload
                          customRequest={({ file }) => {
                            setAddFileModal({ ...addFileModal, file });
                          }}
                          accept="application/pdf"
                          multiple={false}
                          disabled={!addFileModal.fileType}
                        >
                          <Button
                            disabled={!addFileModal?.fileType}
                            className={`upload-pdf-button ${
                              !addFileModal?.fileType ? 'active' : ''
                            }`}
                          >
                            📁 Select File
                          </Button>
                        </Upload>
                        <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#666' }}>
                          {addFileModal.file ? (
                            <span style={{ color: '#00795C', fontWeight: 500 }}>
                              ✓ {addFileModal.file.name}
                            </span>
                          ) : (
                            <>
                              <span>Max file size: 500 KB</span>
                              <br />
                              <span>Supported: PDF only</span>
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </TabPane>
              </Tabs>
            </Modal>
          )}


        {/* Order Info Modal */}
        {
          orderInfoModal && orderInfoModal.visible && (
            <Modal
              open={orderInfoModal.visible}
              onCancel={closeOrdInfo}
              footer={[
                <Button key="cancel" onClick={closeOrdInfo}>
                  Cancel
                </Button>
              ]}
              width={700}
            >
              <OrderInfoPopup
                data={orderInfoModal.data}
                closeModal={closeOrdInfo}
                refreshTable={() => filterResults(filters)}
              />
            </Modal>
          )
        }

        {/* PDF Viewer Modal */}
        {
          viewerModal && (
            <Modal
              width={1000}
              open={viewerModal?.visible}
              onCancel={() => { setViewerModal({ visible: false }) }}
              okButtonProps={{ style: { display: 'none' } }}
              style={{ top: 20 }}
              styles={{
                body: {
                  height: '85vh',
                  maxHeight: '85vh',
                  overflow: 'hidden',
                  padding: 0
                }
              }}
            >
              <div style={{ height: '100%', width: '100%', overflow: 'auto' }}>
                <PdfViewer
                  order_id={viewerModal?.order_id}
                  patient_id={viewerModal?.patient_id}
                  pdfArrayBuffer={viewerModal?.data}
                  patientData={viewerModal?.patientData}
                />
              </div>
            </Modal>
          )
        }

        {/* Share Study Modal */}
        {selectedStudyForShare && (
          <ShareStudyModal
            visible={shareModalVisible}
            onCancel={() => {
              setShareModalVisible(false);
              setSelectedStudyForShare(null);
            }}
            studyUID={selectedStudyForShare?.ps_study_uid}
            studyDescription={selectedStudyForShare?.pacs_order?.po_diag_desc}
            patientName={selectedStudyForShare?.pacs_order?.patient?.pat_name}
            patientGender={selectedStudyForShare?.pacs_order?.patient?.pat_sex}
            patientDOB={selectedStudyForShare?.pacs_order?.patient?.pat_dob}
            studyDate={selectedStudyForShare?.ps_study_dt_tm}
          />
        )}

        {/* Download Study Modal */}
        {selectedStudyForDownload && (
          <DownloadStudyModal
            visible={downloadModalVisible}
            onCancel={() => {
              setDownloadModalVisible(false);
              setSelectedStudyForDownload(null);
            }}
            studyUID={selectedStudyForDownload?.ps_study_uid}
            studyDescription={selectedStudyForDownload?.pacs_order?.po_diag_desc}
            patientName={selectedStudyForDownload?.pacs_order?.patient?.pat_name}
            accessionNumber={selectedStudyForDownload?.pacs_order?.po_acc_no}
            studyData={selectedStudyForDownload}
          />
        )}

        {/* Series Selection Modal */}
        <Modal
          title="Select Series to Open"
          open={seriesSelectionModal.visible}
          onCancel={() => setSeriesSelectionModal({
            visible: false,
            studyUID: null,
            record: null,
            seriesList: [],
            selectedSeries: [],
            loading: false,
          })}
          footer={[
            <Button
              key="select-all"
              onClick={handleSelectAllSeries}
              disabled={seriesSelectionModal.loading}
            >
              Select All
            </Button>,
            <Button
              key="deselect-all"
              onClick={handleDeselectAllSeries}
              disabled={seriesSelectionModal.loading}
            >
              Deselect All
            </Button>,
            <Button
              key="cancel"
              onClick={() => setSeriesSelectionModal({
                visible: false,
                studyUID: null,
                record: null,
                seriesList: [],
                selectedSeries: [],
                loading: false,
              })}
            >
              Cancel
            </Button>,
            <Button
              key="open"
              type="primary"
              disabled={seriesSelectionModal.selectedSeries.length === 0 || seriesSelectionModal.loading}
              onClick={handleOpenSelectedSeries}
            >
              Open ({seriesSelectionModal.selectedSeries.length} series)
            </Button>,
          ]}
          width={900}
          style={{ top: 40 }}
          bodyStyle={{ maxHeight: '600px', overflowY: 'auto' }}
        >
          <Table
            dataSource={seriesSelectionModal.seriesList}
            loading={seriesSelectionModal.loading}
            rowKey="seriesUID"
            pagination={false}
            scroll={{ y: 450 }}
            size="middle"
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys: seriesSelectionModal.selectedSeries,
              onChange: (selectedRowKeys) => {
                setSeriesSelectionModal(prev => ({
                  ...prev,
                  selectedSeries: selectedRowKeys,
                }));
              },
            }}
            onRow={(record) => ({
              onClick: () => toggleSeriesSelection(record.seriesUID),
              style: { cursor: 'pointer' },
            })}
            columns={[
              {
                title: 'Series No',
                dataIndex: 'seriesNumber',
                key: 'seriesNumber',
                width: 100,
                align: 'center',
                render: (num) => <Typography.Text strong>{num}</Typography.Text>,
              },
              {
                title: 'Description',
                dataIndex: 'seriesDescription',
                key: 'seriesDescription',
                ellipsis: true,
                render: (text) => text || 'No description',
              },
              {
                title: 'Body Part',
                dataIndex: 'bodyPart',
                key: 'bodyPart',
                width: 150,
                align: 'center',
                render: (text) => text || '-',
              },
              {
                title: 'Images',
                dataIndex: 'numInstances',
                key: 'numInstances',
                width: 100,
                align: 'center',
                render: (num) => (
                  <Typography.Text type={num === 0 ? 'secondary' : undefined}>
                    {num > 0 ? num : '0'}
                  </Typography.Text>
                ),
              },
            ]}
            locale={{
              emptyText: 'No series found for this study',
            }}
          />
        </Modal>

        {/* Edit Signed-off Report Modal */}
        {
          editReportModal && editReportModal.visible && (
            <Modal
              title="Edit Signed-off Report"
              width={'62.5rem'}
              open={editReportModal.visible}
              onCancel={() => { setEditReportModal({ visible: false, reportData: null, patientData: null }) }}
              cancelText="Cancel"
              okText="Save"
              onOk={async () => {
                try {
                  const response = await makePostCall('/submit-report', {
                    html: editReportModal.reportData?.pr_report_html,
                    yh_no: editReportModal.patientData?.pacs_order?.patient?.pat_pin,
                    order_no: editReportModal.patientData?.pacs_order?.po_his_ord_no,
                    acc_no: editReportModal.patientData?.pacs_order?.po_acc_no,
                    order_id: editReportModal.order_id,
                    user_id: getUserDetails()?.username,
                    status: 'signoff',
                    report_id: editReportModal.reportData?.pr_id,
                    is_editing_signedoff: true,
                    edited_at: new Date().toISOString(),
                    edited_by: getUserDetails()?.username,
                  });

                  if (response.data?.success || response.data?.data) {
                    message.success('Report updated successfully');
                    setEditReportModal({ visible: false, reportData: null, patientData: null });
                    filterResults(filters, pagination.current, pagination.pageSize);
                  } else {
                    message.error(response.data?.message || 'Failed to update report');
                  }
                } catch (error) {
                  console.error('Error updating report:', error);
                  message.error(error.response?.data?.message || 'Failed to update report');
                }
              }}
              style={{ top: '1.25rem' }}
              styles={{
                body: {
                  height: '75vh',
                  maxHeight: '75vh',
                  overflow: 'hidden',
                  padding: 0
                }
              }}
            >
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <TinyEditor
                  initialContent={editReportModal.reportData?.pr_report_html || '<div></div>'}
                  placeholder="Edit report content..."
                  handleChange={(newContent) => {
                    setEditReportModal(prev => ({
                      ...prev,
                      reportData: {
                        ...prev.reportData,
                        pr_report_html: newContent
                      }
                    }));
                  }}
                  fromReporting={false}
                  customHeight="75vh"
                />
              </div>
            </Modal>
          )
        }
      </div>
      </div>
    </div>
    </Spin>
  );
}

export default MyWorklist;
